import { useRef, useState, useCallback, useEffect, useContext } from "react";
import { FACTORS_TO_SHOW } from "@app/service/user";
import {
  ErrorContext,
  MfaErrorContext,
  MfaSetupContext,
  ProfileContext,
} from "@app/provider";
import { useDialog } from "@natera/platform/lib/hooks";
import UpdateMobileNumberDialog from "@app/components/verifyCodeForm/updateMobileNumberDialog";

interface UseVerifyCodeFormParams {
  defaultCounterTime: number;
  defaultTimeLeft: number;
  validationErrorKey: string;
  profile?: {
    email?: string;
    phoneNumber?: string;
  };
  pageName?: string;
}

interface UseVerifyCodeFormReturn {
  timeLeft: number;
  phoneNumber: string;
  updateMobileNumberDialog: ReturnType<typeof useDialog>;
  handleResendCodeClick: (handleResend: () => void) => void;
  handleUpdateMobileNumberClick: () => void;
  resetTimer: () => void;
}

const useVerifyCodeForm = ({
  defaultCounterTime,
  defaultTimeLeft,
  validationErrorKey,
  profile,
  pageName,
}: UseVerifyCodeFormParams): UseVerifyCodeFormReturn => {
  const timer = useRef<number | undefined>(undefined);

  const { parseError } = useContext(MfaErrorContext);
  const { disableUserFactors, enrollUserFactor } = useContext(MfaSetupContext);
  const { updatePhone, getProfile } = useContext(ProfileContext);
  const errorController = useContext(ErrorContext);
  const { clearValidationError } = errorController;

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [lastResendTime, setLastResendTime] = useState(Date.now());
  const [timeLeft, setTimeLeft] = useState(defaultTimeLeft);

  const updateMobileNumberDialog = useDialog(UpdateMobileNumberDialog);

  const checkCounter = useCallback(() => {
    const now = Date.now();
    const diff = now - lastResendTime;

    if (diff > defaultCounterTime) {
      setTimeLeft(0);
      setLastResendTime(0);
      return;
    }
    setTimeLeft(Math.floor((defaultCounterTime - diff) / 1000));
    timer.current = window.setTimeout(checkCounter, 100);
  }, [lastResendTime, defaultCounterTime]);

  useEffect(() => {
    if (lastResendTime !== 0) {
      timer.current = window.setTimeout(checkCounter, 100);
      return () => clearTimeout(timer.current);
    }
  }, [lastResendTime, checkCounter]);

  const resetTimer = useCallback(() => {
    setLastResendTime(Date.now());
    setTimeLeft(defaultTimeLeft);
  }, [defaultTimeLeft]);

  const handleResendCodeClick = useCallback(
    (handleResend: () => void) => {
      clearValidationError(validationErrorKey);
      handleResend();
      setLastResendTime(Date.now());
    },
    [clearValidationError, validationErrorKey]
  );

  const handleUpdatePhoneNumber = useCallback(
    async (newPhoneNumber: string) => {
      try {
        await disableUserFactors();
        await enrollUserFactor(FACTORS_TO_SHOW.sms, "OKTA", {
          phoneNumber: newPhoneNumber,
        });

        await updatePhone(newPhoneNumber);
        await getProfile();

        setPhoneNumber(newPhoneNumber);
      } catch (error) {
        await parseError(error.errorCode, error.errorSummary, errorController);
      } finally {
        setLastResendTime(Date.now());
        setTimeLeft(defaultTimeLeft);

        updateMobileNumberDialog.close();
      }
    },
    [
      disableUserFactors,
      enrollUserFactor,
      updatePhone,
      getProfile,
      parseError,
      errorController,
      defaultTimeLeft,
      updateMobileNumberDialog,
    ]
  );

  const handleUpdateMobileNumberClick = useCallback(() => {
    const dialogProps: {
      currentPhone: string;
      handleSave: (newPhoneNumber: string) => Promise<void>;
      handleBack: () => void;
      pageName?: string;
    } = {
      currentPhone: profile?.phoneNumber ?? "",
      handleSave: handleUpdatePhoneNumber,
      handleBack: updateMobileNumberDialog.close,
    };

    if (pageName) {
      dialogProps.pageName = pageName;
    }

    updateMobileNumberDialog.open(dialogProps);
  }, [
    profile?.phoneNumber,
    handleUpdatePhoneNumber,
    updateMobileNumberDialog,
    pageName,
  ]);

  return {
    timeLeft,
    phoneNumber,
    updateMobileNumberDialog,
    handleResendCodeClick,
    handleUpdateMobileNumberClick,
    resetTimer,
  };
};

export default useVerifyCodeForm;
